import { supabase } from "@/utils/supabase/client";

/**
 * Get a specific connected account token from user settings
 * @param {string} userId - User ID
 * @param {string} provider - Provider name (e.g., 'dribbble', 'spotify', etc.)
 * @returns {Promise<string|null>} The access token or null if not found
 */
export async function getConnectedAccountToken(userId, provider) {
  try {
    const { data, error } = await supabase
      .from("user_settings")
      .select("connected_accounts")
      .eq("id", userId)
      .single();

    if (error || !data) {
      return null;
    }

    return data.connected_accounts?.[provider] || null;
  } catch (error) {
    console.error(`Error getting ${provider} token:`, error);
    return null;
  }
}

/**
 * Set a connected account token in user settings
 * @param {string} userId - User ID
 * @param {string} provider - Provider name (e.g., 'dribbble', 'spotify', etc.)
 * @param {string} token - Access token to store
 * @returns {Promise<boolean>} Success status
 */
export async function setConnectedAccountToken(userId, provider, token) {
  try {
    // Get current connected accounts
    const { data: currentSettings } = await supabase
      .from("user_settings")
      .select("connected_accounts")
      .eq("id", userId)
      .single();

    const connectedAccounts = currentSettings?.connected_accounts || {};
    connectedAccounts[provider] = token;

    const { error } = await supabase.from("user_settings").upsert({
      id: userId,
      connected_accounts: connectedAccounts,
      updated_at: new Date().toISOString(),
    });

    return !error;
  } catch (error) {
    console.error(`Error setting ${provider} token:`, error);
    return false;
  }
}

/**
 * Remove a connected account token from user settings
 * @param {string} userId - User ID
 * @param {string} provider - Provider name (e.g., 'dribbble', 'spotify', etc.)
 * @returns {Promise<boolean>} Success status
 */
export async function removeConnectedAccountToken(userId, provider) {
  try {
    // Get current connected accounts
    const { data: currentSettings } = await supabase
      .from("user_settings")
      .select("connected_accounts")
      .eq("id", userId)
      .single();

    if (!currentSettings?.connected_accounts) {
      return true; // Nothing to remove
    }

    const connectedAccounts = { ...currentSettings.connected_accounts };
    delete connectedAccounts[provider];

    const { error } = await supabase.from("user_settings").upsert({
      id: userId,
      connected_accounts: connectedAccounts,
      updated_at: new Date().toISOString(),
    });

    return !error;
  } catch (error) {
    console.error(`Error removing ${provider} token:`, error);
    return false;
  }
}

/**
 * Check if a user has a specific connected account
 * @param {Object} userSettings - User settings object (can be from session)
 * @param {string} provider - Provider name (e.g., 'dribbble', 'spotify', etc.)
 * @returns {boolean} Whether the account is connected
 */
export function hasConnectedAccount(userSettings, provider) {
  return !!userSettings?.connected_accounts?.[provider];
}

/**
 * Get all connected accounts for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Connected accounts object or null
 */
export async function getAllConnectedAccounts(userId) {
  try {
    const { data, error } = await supabase
      .from("user_settings")
      .select("connected_accounts")
      .eq("id", userId)
      .single();

    if (error || !data) {
      return null;
    }

    return data.connected_accounts || {};
  } catch (error) {
    console.error("Error getting connected accounts:", error);
    return null;
  }
}
